import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { ApprovalActions } from '@app/enums';
import { ApprovalInstanceService, AuthService, NotifyService } from '@app/services';
import { TranslatableKey } from '@app/types/translatable.type';
import {
    ChangeRequestTemplateDisplayOptions,
    ChangeRequestTemplateFieldOptions,
} from '@app/types/translatables/change-request-template-display.options';
import { ChangeRequestApprovalInstanceFlowComponent } from '@change-requests/components';
import { ChangeRequestService } from '@change-requests/services/change-request.service';
import { IndexView } from '@common/views';
import { ChangeRequest } from '@models/change-requests/change-request.model';

@Component({
    templateUrl: './change-request-show.template.html',
    styleUrls: ['./change-request-show.style.scss'],
})
export class ChangeRequestShowView extends IndexView {
    @ViewChild('approvalFlow') approvalFlow!: ChangeRequestApprovalInstanceFlowComponent;
    @ViewChild('confirmCancelRequest') confirmCancelRequest!: ConfirmationDialogComponent;
    @ViewChild('confirmApprovalAction') confirmApprovalAction!: ConfirmationDialogComponent;

    isLoading = false;
    changeRequest: ChangeRequest;
    canBeCancelled = false;
    actionToConfirm: TranslatableKey = '';
    confirmApprovalActionHeader: TranslatableKey = '';
    approvalActionMessage = '';
    canApprovalActionHaveClosingComment = false;
    approvalActionClosingComment = '';
    changeRequestTemplateFields = ChangeRequestTemplateFieldOptions;
    ChangeRequestTemplateDisplayOptions = ChangeRequestTemplateDisplayOptions;

    status: string;

    approvalActionTitleMap = new Map<ApprovalActions, string>([
        [ApprovalActions.Approve, 'change-requests.actionTitle.approve'],
        [ApprovalActions.Deny, 'change-requests.actionTitle.deny'],
        [ApprovalActions.ForceApprove, 'change-requests.actionTitle.forceApprove'],
        [ApprovalActions.ForceDeny, 'change-requests.actionTitle.forceDeny'],
    ]);
    approvalActionMessageMap = new Map<ApprovalActions, string>([
        [ApprovalActions.Approve, 'change-requests.actionPanel.approve'],
        [ApprovalActions.Deny, 'change-requests.actionPanel.deny'],
        [ApprovalActions.ForceApprove, 'change-requests.actionPanel.forceApprove'],
        [ApprovalActions.ForceDeny, 'change-requests.actionPanel.forceDeny'],
    ]);
    approvalActionMap = new Map<ApprovalActions, string>([
        [ApprovalActions.Approve, 'approve'],
        [ApprovalActions.Deny, 'deny'],
        [ApprovalActions.ForceApprove, 'change-requests.forceApprove'],
        [ApprovalActions.ForceDeny, 'change-requests.forceDeny'],
    ]);

    constructor(
        public auth: AuthService,
        private approvalInstanceService: ApprovalInstanceService,
        private router: Router,
        private route: ActivatedRoute,
        private notifyService: NotifyService,
        private changeRequestService: ChangeRequestService
    ) {
        super();

        this.changeRequest = this.route.snapshot.data?.models?.get('changeRequest');
        this.status = this.changeRequest.isComplete() ? 'Previous' : 'Current';

        if (this.changeRequest.canBeActioned() && this.auth.employee.is(this.changeRequest.requestedBy)) {
            this.canBeCancelled = true;
        }
    }

    async onCancel(): Promise<void> {
        const confirmed = await this.confirmCancelRequest.open();

        if (confirmed) {
            await this.changeRequestService.cancel(this.changeRequest);

            this.canBeCancelled = false;

            this.notifyService.success('change-requests.requestCancelled');

            await this.router.navigate(['..'], { relativeTo: this.route });
        }
    }

    async confirmAction(action: ApprovalActions): Promise<void> {
        this.actionToConfirm = this.approvalActionMap.get(action) || '';
        this.confirmApprovalActionHeader = this.approvalActionTitleMap.get(action) || '';
        this.approvalActionMessage = this.approvalActionMessageMap.get(action) || '';
        const isApproveAction = action === ApprovalActions.Approve;

        this.canApprovalActionHaveClosingComment = !isApproveAction;
        this.confirmApprovalAction.primaryButtonType = isApproveAction ? 'primary' : 'danger';

        const confirmed = await this.confirmApprovalAction.open();

        try {
            if (confirmed) {
                await this.commitAction(action);
                this.notifyService.success({ key: 'change-requests.actionSuccess', params: { action: action } });

                const updatedChangeRequest = await ChangeRequest.with([
                    'fieldValues.field',
                    'cancelledBy',
                    'requestedBy',
                    'subject',
                    'template',
                    'approvalInstance',
                    'approvalInstance.forcedBy',
                    'approvalInstance.steps',
                    'approvalInstance.steps.employee',
                ]).find(this.changeRequest.id);

                if (!updatedChangeRequest.canBeActioned()) {
                    this.canBeCancelled = false;
                }

                this.approvalFlow.updateApprovalFlow(this.auth.employee, updatedChangeRequest);
            }
        } catch (e: unknown) {
            const error = e as { error?: { errors?: Array<{ detail: string }> } };
            this.notifyService.error(error.error?.errors?.[0]?.detail || 'change-requests.requestError');
        }
    }

    private commitAction(action: ApprovalActions): Promise<void> {
        switch (action) {
            case ApprovalActions.Approve:
                return this.approvalInstanceService.approve(this.changeRequest.approvalInstance);
            case ApprovalActions.Deny:
                return this.approvalInstanceService.deny(
                    this.changeRequest.approvalInstance,
                    this.approvalActionClosingComment
                );
            case ApprovalActions.ForceApprove:
                return this.approvalInstanceService.forceApprove(
                    this.changeRequest.approvalInstance,
                    this.approvalActionClosingComment
                );
            case ApprovalActions.ForceDeny:
                return this.approvalInstanceService.forceDeny(
                    this.changeRequest.approvalInstance,
                    this.approvalActionClosingComment
                );
            default:
                return Promise.resolve();
        }
    }
}
